import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { getSettingsState } from '@containers/settings/settings.selectors'
import { Store, select } from '@ngrx/store'
import Feature from 'ol/Feature'
import Map from 'ol/Map'
import View from 'ol/View'
import GeoJSON from 'ol/format/GeoJSON'
import { Image as ImageLayer, Vector as VectorLayer } from 'ol/layer'
import { ImageWMS, Vector as VectorSource } from 'ol/source'
import { Fill, Stroke, Style } from 'ol/style'
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs'
import { environment } from '@env/environment'
import FillPattern from 'ol-ext/style/FillPattern.js'
import { getMobileSettingsState } from '@containers/mobile-settings/mobile-settings.selectors'
import { FetchActiveLayersRequest } from '@shared/shared.actions'
import { getActiveLayers } from '@shared/shared.select'
import * as extent from 'ol/extent'
import { Overlay } from 'ol'
import { toStringHDMS } from 'ol/coordinate'

@Component({
  selector: 'active-layer-map',
  templateUrl: './active-layer-map.component.html',
  styleUrls: ['./active-layer-map.component.css'],
})
export class ActiveLayerMapComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  public map: Map
  public overlay: Overlay
  public vector: VectorLayer<any>
  public raster: ImageLayer<any>
  public settings: any
  private settings$: Subscription
  public extent: any
  public resolution: any
  public mobileSettings$: Subscription
  public interval: any
  public selectionInterval: any
  @Input() public selectedZones: any = []
  @Output() onSelectionChange: EventEmitter<any> = new EventEmitter()
  public activeLayersState$: Subscription
  public activeLayer: any
  private mapSubject = new BehaviorSubject<boolean>(false)
  private activeLayerSubject = new BehaviorSubject<boolean>(false)
  private activeLayerIdSubject = new BehaviorSubject<boolean>(false)
  public subjectSubscription: Subscription
  public activeLayerId: number
  private olExtent = extent
  @ViewChild('olPopup', { static: false }) popup: ElementRef

  constructor(public store: Store<any>) {}

  ngOnInit() {
    this.subjectSubscription = combineLatest([
      this.mapSubject,
      this.activeLayerSubject,
      this.activeLayerIdSubject,
    ]).subscribe(([map, activeLayer, activeLayerId]) => {
      if (map && activeLayerId && activeLayer) {
        this.addLayerToMap(this.activeLayerId)
      }
    })

    this.activeLayersState$ = this.store
      .pipe(select(getActiveLayers))
      .subscribe((state) => {
        if (state.size) {
          const activeLayer = state.get(0).toJS()

          if (activeLayer && !this.activeLayer) {
            this.activeLayer = activeLayer
            this.activeLayerSubject.next(true)
          }
        }
      })
    this.mobileSettings$ = this.store
      .pipe(select(getMobileSettingsState))
      .subscribe((state) => {
        const activeLayerId = state.getIn([
          'data',
          'SETTINGS_MOBILE_RESPONSIBILITY_LAYER',
        ])

        if (activeLayerId && !this.activeLayerId) {
          this.activeLayerId = activeLayerId
          this.activeLayerIdSubject.next(true)
          this.store.dispatch<any>(
            new FetchActiveLayersRequest({
              filters: {
                matchOr: [{ field: 'id', value: this.activeLayerId }],
              },
            }),
          )
        }
      })
  }

  setOpacity = (hex: string, alpha: number) =>
    hex
      ? `${hex}${Math.floor(alpha * 255)
          .toString(16)
          .padStart(2)}`
      : null

  addLayerToMap(activeLayerId: number) {
    const _this = this

    const source = new VectorSource({
      format: new GeoJSON(),
      url: `${environment.API_ENDPOINT}/gis/public/layer/${activeLayerId}`,
    })

    source.on('featuresloadend', (e) => {
      if (e.features.length) {
        _this.updateSelectedZones()
      }
    })

    const style = new Style({
      stroke: new Stroke({
        color: _this.setOpacity(this.activeLayer.color, 0.8),
        width: 1,
      }),
      fill: new Fill({
        color: _this.setOpacity(this.activeLayer.color, 0.3),
      }),
    })
    this.vector = new VectorLayer({
      visible: true,
      source,
      style,
    })

    _this.map.addLayer(this.vector)
  }

  ngAfterViewInit() {
    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        this.settings = state.get('data')
        if (this.settings.size && !this.map) {
          this.initMap()
        }
      })
  }

  ngOnDestroy() {
    this.settings$.unsubscribe()
    this.mobileSettings$.unsubscribe()
    this.subjectSubscription.unsubscribe()
    this.activeLayersState$.unsubscribe()
  }

  setSelectedStyle(feature: Feature) {
    feature.setStyle(
      new Style({
        stroke: new Stroke({
          color: '#485fc7',
          width: 2,
        }),
        fill: new FillPattern({
          pattern: 'hatch',
          color: '#485fc7',
          scale: 1,
          size: 1,
          spacing: 3,
          angle: 45,
          fill: new Fill({
            color: this.setOpacity('#485fc7', 0.4),
          }),
        }),
      }),
    )
  }

  setDefaultStyle(feature: Feature) {
    const style = new Style({
      stroke: new Stroke({
        color: this.setOpacity(this.activeLayer.color, 0.8),
        width: 1,
      }),
      fill: new Fill({
        color: this.setOpacity(this.activeLayer.color, 0.3),
      }),
    })

    feature.setStyle(style)
  }

  fitMapToSelectedZones(selectedFeatures: Feature[]) {
    let combinedExtent = extent.createEmpty()

    selectedFeatures.forEach((feature) => {
      extent.extend(combinedExtent, feature.getGeometry().getExtent())
    })

    if (combinedExtent.every((c) => Number.isFinite(c))) {
      this.map.getView().fit(combinedExtent, {
        size: this.map.getSize(),
        padding: [20, 20, 20, 20],
        maxZoom: 18,
      })
    }
  }

  updateSelectedZones() {
    const source = this.vector.getSource() as VectorSource
    const features = source.getFeatures()
    const selectedFeatures = features.filter((f: Feature) => {
      return this.selectedZones.find(
        ({ id }) => f.getPropertiesInternal().id === id,
      )
    })

    features.forEach((f: Feature) => {
      const isSelected = this.selectedZones.find(
        ({ id }) => f.getPropertiesInternal().id === id,
      )

      if (isSelected) {
        this.setSelectedStyle(f)
      } else {
        this.setDefaultStyle(f)
      }
    })

    this.fitMapToSelectedZones(selectedFeatures)
  }

  initMap() {
    const {
      GIS_GEOSERVER_URL,
      GIS_GEOSERVER_WORKSPACE,
      GIS_BASE_LAYER,
      GIS_MAP_EXTENT,
    } = this.settings.toJS().gis

    const extent = GIS_MAP_EXTENT.split(',').map((c: string) => parseFloat(c))
    const origin = extent[0] + ',' + extent[1]

    this.raster = new ImageLayer({
      source: new ImageWMS({
        url: `${GIS_GEOSERVER_URL}/${GIS_GEOSERVER_WORKSPACE}/wms`,
        params: {
          FORMAT: 'image/jpeg',
          VERSION: '1.1.1',
          tiled: true,
          LAYERS: `${GIS_GEOSERVER_WORKSPACE}:${GIS_BASE_LAYER}`,
          exceptions: 'application/vnd.ogc.se_inimage',
          tilesOrigin: origin,
        },
      }),
    })

    this.overlay = new Overlay({
      element: this.popup.nativeElement,
      autoPan: false,
    })

    this.map = new Map({
      layers: [this.raster],
      target: 'ol-active-layer-map',
      overlays: [this.overlay],
      view: new View({
        projection: 'EPSG:4326',
        center: [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
        zoom: 13,
      }),
    })

    this.map.on('click', ({ pixel }) => {
      const feature = this.map.forEachFeatureAtPixel(pixel, function (feature) {
        return feature
      }) as Feature

      if (feature) {
        let selectedZones = []
        const { geometry, ...data } = feature.getPropertiesInternal()

        const isZoneSelected = this.selectedZones.find(
          ({ id }) => id === data.id,
        )

        if (isZoneSelected) {
          selectedZones = this.selectedZones.filter(({ id }) => id !== data.id)
        } else {
          selectedZones = [
            ...this.selectedZones,
            {
              calculation: this.activeLayer.calculation,
              layerId: data.layer_id,
              ...data,
            },
          ]
        }

        this.onSelectionChange.emit(selectedZones)
      }
    })

    this.map.on('pointermove', (event) => {
      const feature = this.map.forEachFeatureAtPixel(
        event.pixel,
        (feat) => feat,
      )
      const element = this.overlay.getElement()
      if (feature) {
        const coordinate = event.coordinate
        const geometry = feature.getGeometry() as any
        const hdms = toStringHDMS(coordinate)
        const data = feature.getPropertiesInternal()
        element.innerHTML = data.label

        const centroid = this.olExtent.getCenter(geometry.getExtent())
        this.overlay.setPosition(centroid)
        element.style.display = 'block'
      } else {
        element.style.display = 'none'
      }
    })

    this.mapSubject.next(true)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedZones) {
      const currentZoneIds = changes.selectedZones.currentValue
        ? changes.selectedZones.currentValue.map((z: any) => z.id)
        : []
      const prevZoneIds = changes.selectedZones.previousValue
        ? changes.selectedZones.previousValue.map((z: any) => z.id)
        : []

      if (JSON.stringify(currentZoneIds) !== JSON.stringify(prevZoneIds)) {
        if (this.map && this.activeLayer && this.vector) {
          this.updateSelectedZones()
        }
      }
    }
  }
}
